/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { Link } from '@/components/link';
import { useAnalytics } from '@/hooks/use-analytics';
import { searchUrlBuilder } from '@/lib/url';
import type { SearchPreviewProduct, SearchPreviewResult } from '@/lib/search/types';
import SearchProductCard from './search-product-card';

interface SearchDropdownProps {
    preview: SearchPreviewResult;
    /** What the shopper typed; used to highlight the matching prefix of each suggestion. */
    query: string;
    /** Called when the shopper picks any link in the dropdown, before navigation. */
    onNavigate: () => void;
}

/** Renders the typed prefix of a suggestion as-is and the completion muted (mobile design only). */
function HighlightedSuggestion({ phrase, query }: { phrase: string; query: string }): ReactElement {
    const prefixLength = phrase.toLowerCase().startsWith(query.toLowerCase()) ? query.length : 0;
    return (
        <>
            {phrase.slice(0, prefixLength)}
            <span className="text-ps-disabled-text md:text-inherit">{phrase.slice(prefixLength)}</span>
        </>
    );
}

/**
 * Header search dropdown (PRIC-66): "Sugerencias" (text suggestions) and "Productos principales" (top products)
 * with a link to the full search results. Desktop shows both sections side by side; mobile stacks them.
 */
export default function SearchDropdown({ preview, query, onNavigate }: SearchDropdownProps): ReactElement {
    const { t } = useTranslation('search');
    const { currency: siteCurrency } = useSite();
    const analytics = useAnalytics();
    const term = query.trim();
    const { suggestions, products, total } = preview;
    const currency = preview.currency ?? siteCurrency;
    const hasSuggestions = suggestions.length > 0;
    const hasProducts = products.length > 0;

    const lastTrackedRef = useRef<SearchPreviewResult | null>(null);
    useEffect(() => {
        if (lastTrackedRef.current === preview || (!hasSuggestions && !hasProducts)) return;
        lastTrackedRef.current = preview;
        void analytics.trackViewSearchSuggestions({
            searchInputText: preview.searchPhrase,
            suggestions: [...suggestions, ...products.map((product) => product.name)],
        });
    }, [analytics, preview, suggestions, products, hasSuggestions, hasProducts]);

    const trackClick = (suggestion: string) => {
        void analytics.trackClickSearchSuggestion({ searchInputText: term, suggestion });
        onNavigate();
    };

    const viewAllLink = hasProducts && (
        <Link
            to={searchUrlBuilder(term)}
            onClick={onNavigate}
            data-slot="search-dropdown-link"
            className="text-xs leading-[22px] text-ps-text-link hover:underline md:text-center">
            {total > 0 ? t('dropdown.viewAll', { count: total }) : t('dropdown.viewAllNoCount')}
        </Link>
    );

    return (
        <div className="flex flex-col gap-4 px-4 py-6 md:flex-row md:gap-8 md:p-6" data-testid="search-dropdown">
            {hasSuggestions && (
                <section
                    className="flex flex-col gap-2 md:w-[218px] md:shrink-0 md:gap-3.5"
                    aria-labelledby="search-dropdown-suggestions">
                    <h2
                        id="search-dropdown-suggestions"
                        className="text-sm font-medium leading-[22px] text-ps-black md:text-lg">
                        {t('dropdown.suggestions')}
                    </h2>
                    <p className="hidden text-base leading-[22px] text-ps-gray-500 md:block">{term}</p>
                    <ul className="flex flex-col gap-2 md:gap-3">
                        {suggestions.map((phrase) => (
                            <li key={phrase}>
                                <Link
                                    to={searchUrlBuilder(phrase)}
                                    onClick={() => trackClick(phrase)}
                                    data-slot="search-dropdown-link"
                                    className="block text-xs leading-[22px] text-ps-black hover:underline md:pl-2 md:text-base">
                                    <HighlightedSuggestion phrase={phrase} query={term} />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {hasSuggestions && <div className="hidden w-px shrink-0 self-stretch bg-ps-border md:block" />}

            {hasProducts ? (
                <section className="flex flex-col gap-4 md:gap-6" aria-labelledby="search-dropdown-products">
                    <div className="flex flex-col gap-4">
                        <h2
                            id="search-dropdown-products"
                            className="text-sm font-medium leading-[22px] text-ps-black md:text-lg">
                            {t('dropdown.topProducts')}
                        </h2>
                        <ul className="flex flex-col gap-4 md:flex-row">
                            {products.map((product: SearchPreviewProduct) => (
                                <li key={product.id}>
                                    <SearchProductCard
                                        product={product}
                                        currency={currency}
                                        onSelect={(selected) => trackClick(selected.name)}
                                    />
                                </li>
                            ))}
                        </ul>
                    </div>
                    {viewAllLink}
                </section>
            ) : (
                <p className="text-sm text-ps-gray-500 md:self-center" role="status" data-testid="search-no-results">
                    {t('dropdown.noResults', { term })}
                </p>
            )}
        </div>
    );
}
